/**
 * Grade bands — how a `GRADE_SCALE` (Font) index lands on a gym's difficulty
 * levels.
 *
 * A route carries a gym-local difficulty key (a colour band); a Spraywall
 * problem carries a Font grade index. Every statistic that puts the two on one
 * axis (the pyramid, the volume bars, the intensity zones, the climbing level,
 * the admin distribution) needs the same answer to "which band is this grade
 * in", so it lives here once — the client list filter and the Cloud Functions
 * read the same rule.
 *
 * Spec: docs/spec/02d-shared-spraywall.md ("Grade bands (gradeBands.js)").
 */
import { GRADE_SCALE } from "./gradeScale.js";

// The fixed Font bands used when the gym has no difficulty level with a gradeRange.
// Rung 15 (7b) is deliberately in none of them: the labels are literal, and such a
// problem is visible under "Mind" only.
export const FALLBACK_BANDS = Object.freeze([
  { key: "3-5c", from: "3", to: "5c" },
  { key: "6a-6b+", from: "6a", to: "6b+" },
  { key: "6c-7a+", from: "6c", to: "7a+" },
  { key: "7b+", from: "7b+", to: null },
]);

function fontIndex(font) {
  return GRADE_SCALE.findIndex((g) => g.font === font);
}

function levelOrder(level) {
  return Number.isFinite(level?.order) ? level.order : Infinity;
}

/**
 * The bands of a gym: one per difficulty level that has a `gradeRange` (by
 * `order`, then key), or the four fixed Font bands when none has one. A level
 * band's `key` is the gym's difficulty key, so it shares the key space of the
 * route statistics; a fallback band (`fallback: true`) matches no route key.
 * @returns {Array<{ key: string, label: string, color: string|null, border: string|null, tc: string|null, min: number, max: number, fallback: boolean }>}
 */
export function gradeBands(difficultyConfig) {
  const levels = [];
  if (difficultyConfig && typeof difficultyConfig === "object" && !Array.isArray(difficultyConfig)) {
    for (const [key, level] of Object.entries(difficultyConfig)) {
      const range = level?.gradeRange;
      if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) continue;
      levels.push({ key, level });
    }
  }
  if (levels.length > 0) {
    levels.sort((a, b) => {
      const byOrder = levelOrder(a.level) - levelOrder(b.level);
      if (byOrder !== 0 && !Number.isNaN(byOrder)) return byOrder;
      return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });
    return levels.map(({ key, level }) => ({
      key,
      label: typeof level.label === "string" ? level.label : key,
      color: level.color ?? null,
      // The ring of the dot, as the map draws a difficulty level (DifficultyPill).
      border: level.border ?? null,
      tc: level.tc ?? null,
      min: level.gradeRange.min,
      max: level.gradeRange.max,
      fallback: false,
    }));
  }
  const bands = [];
  for (const def of FALLBACK_BANDS) {
    const min = fontIndex(def.from);
    const max = def.to === null ? GRADE_SCALE.length - 1 : fontIndex(def.to);
    if (min < 0 || max < 0) continue;
    bands.push({
      key: def.key,
      label: def.to === null ? def.from : `${def.from}–${def.to}`,
      color: null,
      border: null,
      tc: null,
      min,
      max,
      fallback: true,
    });
  }
  return bands;
}

/**
 * The first band whose range contains the rung, or null (also for a non-integer
 * rung). Ranges are not exclusive; the bands come sorted by `order`, so the
 * lowest order wins.
 */
export function bandOfGrade(bands, idx) {
  if (!Array.isArray(bands) || !Number.isInteger(idx)) return null;
  return bands.find((b) => b && b.min <= idx && idx <= b.max) || null;
}

/**
 * The gym difficulty level a grade falls in, as `{ key, ...level }`, or null
 * when the gym has no level with a `gradeRange` containing it. Only real
 * levels count here — a fallback Font band is not a difficulty level.
 */
export function difficultyLevelOfGrade(difficultyConfig, idx) {
  const band = bandOfGrade(gradeBands(difficultyConfig).filter((b) => !b.fallback), idx);
  if (!band) return null;
  return { key: band.key, ...difficultyConfig[band.key] };
}
